import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import { paginate, totalPages } from "@/utils/media";

const PAGE_SIZE = 4;
const CATEGORIES = ["全部", "电影新闻", "电视剧资讯", "行业动态"];

export default function NewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "全部";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  usePageMeta({
    title: "影视资讯与专题 - 聚会的目的在线",
    description:
      "聚合电影新闻、电视剧资讯与行业动态，第一时间了解影视作品口碑、票房与创作趋势。",
    keywords: "影视资讯,电影新闻,电视剧资讯,行业动态,聚会的目的在线",
  });

  const filtered =
    category === "全部" ? newsList : newsList.filter((n) => n.category === category);
  const total = totalPages(filtered.length, PAGE_SIZE);
  const currentPage = Math.min(page, total);
  const pageItems = paginate(filtered, currentPage, PAGE_SIZE);

  const setCategory = (c: string) => {
    const params = new URLSearchParams();
    if (c !== "全部") params.set("category", c);
    setSearchParams(params);
  };

  const queryString = category === "全部" ? "" : `?category=${encodeURIComponent(category)}`;

  return (
    <Layout>
      <div className="w-full bg-primary-900 text-background-100">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-primary-200 mb-4">
            <Link to="/" className="hover:text-white cursor-pointer transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">影视资讯</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-white">
            影视资讯与专题
          </h1>
          <p className="mt-3 text-sm md:text-base text-primary-100 max-w-2xl leading-relaxed">
            关注电影与电视剧的最新动态，从票房表现到口碑评价，从创作趋势到行业观察，一页尽览。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                c === category
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              {pageItems.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex flex-col sm:flex-row gap-4 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer overflow-hidden"
                >
                  <div className="relative w-full sm:w-48 md:w-56 shrink-0 aspect-[16/9] sm:aspect-auto overflow-hidden bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} 资讯配图`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 min-w-0 p-4 md:p-5 sm:pl-0 sm:py-5 sm:pr-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-800 text-xs font-medium">
                        {item.category}
                      </span>
                      <span className="text-xs text-foreground-400">{item.date}</span>
                    </div>
                    <h2 className="font-heading font-semibold text-base md:text-lg text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                      {item.title}
                    </h2>
                    <p className="mt-2 text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm text-primary-600 font-medium">
                      阅读全文
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <Pagination
              current={currentPage}
              total={total}
              basePath="/news"
              query={queryString}
            />
          </>
        ) : (
          <EmptyState
            icon="ri-newspaper-line"
            title="该分类暂无资讯"
            description="换个分类看看，或浏览全部影视资讯内容。"
            actionLabel="查看全部资讯"
            onAction={() => setCategory("全部")}
          />
        )}
      </div>
    </Layout>
  );
}